import clsx from 'clsx'
import {FC, ReactNode, useId, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useQuery, useQueryClient} from 'react-query'
import {Link} from 'react-router-dom'
import {useIsOnPremises} from '../../auth/core/useOnPremises'
import {hidesPayments} from '../../install-app/core/androidApp'
import {businessSettingsPath} from '../../online-payment/core/businessSettingsPath'
import {getWallet, walletKey} from '../../online-payment/core/walletApi'
import {WalletShortfallAction} from '../../online-payment/wallet/WalletTopUp'
import {useWalletResume} from '../../online-payment/wallet/walletResume'
import {
  activateServiceFeature,
  canSwitchOff,
  contactSwitchOffConsequence,
  findFeature,
  getServiceCatalog,
  isAdminOnlyFeature,
  isPaidTariff,
  isPricedTariff,
  serviceCatalogKey,
  ServiceFeatureKey,
  setServiceAutoRenew,
  switchOffConsequence,
  switchOffEffect,
  switchOffServiceFeature,
} from '../core/catalogApi'
import {
  activationTitle,
  BillingMethod,
  CHARGE_CONFIRM,
  daysLeft,
  fa,
  faPercent,
  isContactTariff,
  needsContact,
  offersMethods,
  readServiceError,
  RENEW_EARLY_DAYS,
  ServicePeriod,
  ServiceTariff,
  tariffParts,
  tariffPricingNote,
  WALLET_INSUFFICIENT,
} from '../core/serviceApi'
import {ChargeConfirmDialog, ChargePrompt, SERVICE_READ_ONLY_TEXT} from './ServiceParts'
import {OfferPrice, ServiceBadge} from './ServiceBadge'
import {ContactNote, ServiceRequestLink} from './SupportContact'
import {ServiceIntroLink} from './ServiceIntroLink'
import {operationAllowance, usedLabel} from '../core/allowance'

type Props = {
  companyId: number
  feature: ServiceFeatureKey
  /** One line on what the service gives, above its price. */
  intro?: ReactNode
  /** The «فعال‌سازی» button; off where the page activates the service with its own save. */
  activation?: boolean
  /** The card's name; the service's own label by default. A page already titled with the service names the price. */
  title?: ReactNode
  /** The «سرویس ویژه» gem; off where the page's title carries it or everything on the page is a paid service. */
  badge?: boolean
  /** «هزینه‌ها از کیف پول کسب‌وکار کم می‌شود»; off where the page says it once for all its cards. */
  walletNote?: boolean
  /**
   * A free service is shown too while it is activated (backend 1.0.0.651+), so that it can be switched off: «سرویس‌ها
   * و تعرفه‌ها». Elsewhere a free service shows nothing unless it is switched off.
   */
  showFree?: boolean
  /** «غیرفعال کردن» of an active service (backend 1.0.0.651+). */
  switchOff?: boolean
  /**
   * Where an operation starts (2026-10-19): an operation that costs nothing now shows one line, «رایگان», which opens on
   * the free allowance, what was used, what is left and the price; one that costs something shows the card in full.
   */
  collapseWhenFree?: boolean
  /** How many uses the operation makes, when the page knows (the invoices of an export). */
  uses?: number
  /** What already counts against a total allowance (the active users, the print templates). */
  used?: number
  className?: string
}

/** The step a short wallet goes on with after the charge: this card's own «فعال‌سازی» or «تمدید». */
const resumeAction = (feature: string) => `service-activation:${feature}`

/**
 * «دورهٔ اشتراک» (user request 2026-10-18: «دوره های پرداخت موازی بابت ماهیانه و سه ماهه و ... با قیمت های متفاوت»): the
 * periods the subscription can be bought for, each with its fee for this business and its monthly cost.
 */
export const PeriodPicker: FC<{
  name: string
  label: string
  periods: ServicePeriod[]
  value?: number
  disabled?: boolean
  onChange: (months: number) => void
}> = ({name, label, periods, value, disabled, onChange}) => {
  // The monthly cost of the shortest period, to say what a longer one saves.
  const base = periods[0]?.perMonth || 0
  return (
    <fieldset className='svc-periods mb-3' disabled={disabled}>
      <legend className='svc-periods__legend'>دورهٔ اشتراک {label}</legend>
      <div className='svc-periods__list'>
        {periods.map((period) => {
          const saves = base > 0 && period.perMonth < base ? Math.round(((base - period.perMonth) / base) * 100) : 0
          const id = `${name}-${period.months}`
          return (
            <label key={period.months} htmlFor={id} className={clsx('svc-period', {'is-on': value === period.months})}>
              <input
                id={id}
                type='radio'
                name={name}
                className='form-check-input'
                checked={value === period.months}
                onChange={() => onChange(period.months)}
              />
              <span className='svc-period__text'>
                <span className='fw-bold'>
                  {period.title || `${fa(period.months)} ماهه`}
                  {period.bought && <span className='badge badge-light-info ms-2'>دورهٔ فعلی</span>}
                </span>
                <span>
                  <OfferPrice amount={period.amount} listAmount={period.listAmount} />
                  {period.due !== period.amount && (
                    <span className='text-success fs-8'> ، برای شما {period.due > 0 ? `${fa(period.due)} ریال` : 'رایگان'}</span>
                  )}
                </span>
                <span className='text-muted fs-8'>
                  ماهانه {fa(period.perMonth)} ریال{saves > 0 ? ` · ${fa(saves)}٪ صرفه‌جویی` : ''}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

type MethodSectionProps = {
  feature: string
  label: string
  tariff: ServiceTariff
  /** What one use takes now for the business (after its free list, gifts and codes). */
  useFee?: number | null
  canChange: boolean
  busy: boolean
  periods?: ServicePeriod[] | null
  choosesPeriod: boolean
  chosenMonths?: number
  onPickMonths: (months: number) => void
  /** The subscription ends within two weeks: «تمدید» from its end. */
  renewable: boolean
  left?: number
  onActivate: (method: BillingMethod, autoRenew: boolean) => void
  onRenew: () => void
  onAutoRenew: (on: boolean) => void
}

/**
 * «روش پرداخت» of a «ترکیبی» service (user request 2026-10-20: «روش‌ها جایگزین هم هستن، مثلن یا ماهیانه یه مبلغی از کیف
 * پول کسر میشه یا به ازای هر بار استفاده یه مبلغی از کیف پول کم میشه»): before the choice (or once the subscription ended)
 * the two ways side by side and nothing works until one is taken; on the subscription, its end and «تمدید خودکار»; per use,
 * the fee of each use and the subscription to take instead.
 */
const MethodSection: FC<MethodSectionProps> = ({
  feature,
  label,
  tariff,
  useFee,
  canChange,
  busy,
  periods,
  choosesPeriod,
  chosenMonths,
  onPickMonths,
  renewable,
  left,
  onActivate,
  onRenew,
  onAutoRenew,
}) => {
  const parts = tariffParts(tariff)
  // A subscription that ended is offered again first; a business that never chose picks one itself.
  const [picked, setPicked] = useState<BillingMethod | undefined>(tariff.expired ? 'subscription' : undefined)
  const [autoRenew, setAutoRenew] = useState(true)
  const [subscribing, setSubscribing] = useState(false)
  const chosenPeriod = periods?.find((period) => period.months === chosenMonths)
  const subscriptionTitle = chosenPeriod ? `اشتراک ${chosenPeriod.title || `${fa(chosenPeriod.months)} ماهه`}` : activationTitle(parts.period)
  const subscriptionFee = chosenPeriod ? chosenPeriod.due : parts.activation
  const useLine = (
    <>
      {tariff.useMeaning || 'هر بار استفاده'}: <OfferPrice amount={parts.use} listAmount={parts.useList} />
      {tariff.usePercent ? <span>، {faPercent(tariff.usePercent)}٪ از هر پرداخت</span> : null}
    </>
  )
  const freeUses = `در طول اشتراک، استفاده رایگان و نامحدود است${tariff.dailyLimit ? `؛ سقف روزانه ${fa(tariff.dailyLimit)} مورد` : ''}.`
  const renewBox = (id: string) => (
    <div className='form-check form-check-custom form-check-sm svc-methods__renew'>
      <input
        id={id}
        type='checkbox'
        className='form-check-input'
        checked={autoRenew}
        disabled={busy}
        onChange={(event) => setAutoRenew(event.target.checked)}
      />
      <label className='form-check-label' htmlFor={id}>
        تمدید خودکار از کیف پول در پایان هر دوره
        <span className='d-block text-muted fs-8'>اگر موجودی کیف پول کافی نباشد، از آن پس به ازای هر بار استفاده حساب می‌شود.</span>
      </label>
    </div>
  )

  if (tariff.method === 'subscription') {
    const switchId = `svc-autorenew-${feature}`
    return (
      <div className='svc-methods'>
        <ul className='svc-activation__prices'>
          <li>
            روش پرداخت: <strong>{activationTitle(parts.period)}</strong>، <OfferPrice amount={parts.activation} listAmount={parts.activationList} />
            {tariff.expiresAtLabel && (
              <span className={clsx('fs-8', renewable ? 'text-warning' : 'text-muted')}>
                {' '}
                ، اعتبار تا {tariff.expiresAtLabel}
                {renewable && left !== undefined && (left > 0 ? ` (${fa(left)} روز مانده)` : ' (امروز)')}
              </span>
            )}
          </li>
          <li>{freeUses}</li>
        </ul>
        {tariff.autoRenew !== null && tariff.autoRenew !== undefined && (
          <div className='form-check form-switch form-check-custom form-check-solid svc-methods__renew'>
            <input
              id={switchId}
              type='checkbox'
              role='switch'
              className='form-check-input'
              checked={tariff.autoRenew}
              disabled={!canChange || busy}
              onChange={(event) => onAutoRenew(event.target.checked)}
            />
            <label className='form-check-label' htmlFor={switchId}>
              تمدید خودکار از کیف پول
              <span className='d-block text-muted fs-8'>
                {tariff.autoRenew
                  ? `در پایان اشتراک، ${fa(parts.activation)} ریال از کیف پول کسر و اشتراک تمدید می‌شود؛ اگر موجودی کافی نباشد، به ازای هر بار استفاده (${fa(parts.use)} ریال) حساب می‌شود.`
                  : `اشتراک${tariff.expiresAtLabel ? ` در ${tariff.expiresAtLabel}` : ''} به پایان می‌رسد و پس از آن باید روش پرداخت را دوباره انتخاب کنید؛ پرداخت به ازای هر بار استفاده (${fa(parts.use)} ریال) از همان زمان در دسترس است.`}
              </span>
            </label>
          </div>
        )}
        {renewable && canChange && (
          <div className='d-flex flex-wrap align-items-center gap-3'>
            <button type='button' className='btn btn-sm btn-light-primary' disabled={busy} onClick={onRenew}>
              {busy && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>}
              تمدید اشتراک {label}
            </button>
            <span className='text-muted fs-8'>تمدید از پایان همین دوره حساب می‌شود؛ روزی از اشتراک کم نمی‌شود.</span>
          </div>
        )}
      </div>
    )
  }

  if (tariff.method === 'perUse') {
    return (
      <div className='svc-methods'>
        <ul className='svc-activation__prices'>
          <li>
            روش پرداخت: <strong>به ازای هر بار استفاده</strong>؛ {useLine}
            {useFee !== undefined && useFee !== null && useFee !== parts.use && (
              <span className='text-success fs-8'>، برای شما {useFee > 0 ? `${fa(useFee)} ریال` : 'رایگان'}</span>
            )}
          </li>
          {tariff.freeQuotaText && <li>{tariff.freeQuotaText}</li>}
          {tariff.dailyLimit ? <li>سقف روزانه: {fa(tariff.dailyLimit)} مورد</li> : null}
        </ul>
        {canChange && !subscribing && (
          <div className='svc-methods__offer'>
            <span>
              با {activationTitle(parts.period)} ({fa(parts.activation)} ریال)، استفاده در طول اشتراک رایگان می‌شود.
            </span>
            <button type='button' className='btn btn-sm btn-light-primary' disabled={busy} onClick={() => setSubscribing(true)}>
              تغییر به اشتراک
            </button>
          </div>
        )}
        {canChange && subscribing && (
          <div className='svc-methods__offer svc-methods__offer--open'>
            {choosesPeriod && periods && (
              <PeriodPicker name={`svc-period-${feature}`} label={label} periods={periods} value={chosenMonths} disabled={busy} onChange={onPickMonths} />
            )}
            {renewBox(`svc-renew-${feature}`)}
            <div className='d-flex flex-wrap gap-2'>
              <button type='button' className='btn btn-sm btn-primary' disabled={busy} onClick={() => onActivate('subscription', autoRenew)}>
                {busy && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>}
                خرید {subscriptionTitle} ({fa(subscriptionFee)} ریال)
              </button>
              <button type='button' className='btn btn-sm btn-light' disabled={busy} onClick={() => setSubscribing(false)}>
                انصراف
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Not chosen yet, or the subscription ended (or the business switched the service off).
  const name = `svc-method-${feature}`
  return (
    <div className='svc-methods'>
      <fieldset className='svc-methods__choice' disabled={!canChange || busy}>
        <legend className='svc-periods__legend'>روش پرداخت {label}</legend>
        <p className='svc-methods__hint'>
          {tariff.expired
            ? 'اشتراک به پایان رسیده است؛ برای ادامه، یکی از دو روش را انتخاب کنید.'
            : 'یکی از دو روش را انتخاب کنید؛ تا انتخاب نکنید، این سرویس کار نمی‌کند.'}
        </p>
        <div className='svc-methods__list'>
          <label htmlFor={`${name}-subscription`} className={clsx('svc-period', {'is-on': picked === 'subscription'})}>
            <input
              id={`${name}-subscription`}
              type='radio'
              name={name}
              className='form-check-input'
              checked={picked === 'subscription'}
              onChange={() => setPicked('subscription')}
            />
            <span className='svc-period__text'>
              <span className='fw-bold'>{activationTitle(parts.period)}</span>
              <span>
                <OfferPrice amount={parts.activation} listAmount={parts.activationList} />
              </span>
              <span className='text-muted fs-8'>{freeUses}</span>
            </span>
          </label>
          <label htmlFor={`${name}-perUse`} className={clsx('svc-period', {'is-on': picked === 'perUse'})}>
            <input
              id={`${name}-perUse`}
              type='radio'
              name={name}
              className='form-check-input'
              checked={picked === 'perUse'}
              onChange={() => setPicked('perUse')}
            />
            <span className='svc-period__text'>
              <span className='fw-bold'>به ازای هر بار استفاده</span>
              <span>{useLine}</span>
              <span className='text-muted fs-8'>
                بدون هزینهٔ فعال‌سازی{tariff.freeQuotaText ? `؛ ${tariff.freeQuotaText}` : ''}.
              </span>
            </span>
          </label>
        </div>
      </fieldset>
      {picked === 'subscription' && canChange && choosesPeriod && periods && (
        <PeriodPicker name={`svc-period-${feature}`} label={label} periods={periods} value={chosenMonths} disabled={busy} onChange={onPickMonths} />
      )}
      {picked === 'subscription' && canChange && renewBox(`svc-renew-${feature}`)}
      {canChange ? (
        <button type='button' className='btn btn-sm btn-primary' disabled={busy || !picked} onClick={() => picked && onActivate(picked, autoRenew)}>
          {busy && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>}
          {picked === 'subscription'
            ? `خرید ${subscriptionTitle} (${fa(subscriptionFee)} ریال)`
            : picked === 'perUse'
              ? 'انتخاب پرداخت به ازای مصرف'
              : 'روش پرداخت را انتخاب کنید'}
        </button>
      ) : (
        <span className='text-muted fs-7'>{SERVICE_READ_ONLY_TEXT}</span>
      )}
    </div>
  )
}

/**
 * A value-added service's price on its own page: the tariff (with the offer's struck-through price), what this
 * business pays after its free list, gifts and codes, whether it is active, «فعال‌سازی» behind a confirmation of the
 * fee — with «افزایش موجودی و فعال‌سازی» when the wallet is short (the activation goes on after the payment) — and
 * «غیرفعال کردن». A service switched off says why; one an administrator switched off cannot be turned on here. A
 * «تماس بگیرید» service shows «تماس بگیرید», the server's sentence and the support line instead of a price and
 * «فعال‌سازی» until an administrator gives the business an access, then «فعال تا …» (support renews it near its end).
 * Shows nothing on a backend without the catalog, for a free service that needs no activation, or on an on-premises
 * install, which has no value-added services.
 */
export const ServiceActivationCard: FC<Props> = ({
  companyId,
  feature,
  intro,
  activation = true,
  title,
  badge = true,
  walletNote = true,
  showFree = false,
  switchOff = true,
  collapseWhenFree = false,
  uses,
  used,
  className,
}) => {
  const queryClient = useQueryClient()
  const onPremises = useIsOnPremises()
  const detailsId = useId()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [prompt, setPrompt] = useState<ChargePrompt>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{
    text: string
    wallet: boolean
    balance?: number
    required?: number
    /** A «تماس بگیرید» service with no access: its request follows (a renewal once the access ended). */
    contact?: boolean
    contactEnded?: boolean
  }>()
  const [renewing, setRenewing] = useState(false)
  const [confirmingOff, setConfirmingOff] = useState(false)
  const [notice, setNotice] = useState('')
  // The subscription period picked among the tariff's (2026-10-18); unset, the one bought last or the tariff's own.
  const [pickedMonths, setPickedMonths] = useState<number>()
  // «ترکیبی» (2026-10-20): the way and «تمدید خودکار» the activation asks for, kept for its confirmation and the wallet's return.
  const [choice, setChoice] = useState<{method?: BillingMethod; autoRenew?: boolean}>({})
  const catalog = useQuery(serviceCatalogKey(companyId), () => getServiceCatalog(companyId), {
    enabled: !onPremises,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  })
  // The balance under the fee's confirmation, so that a short wallet is charged before the fee is agreed to.
  const wallet = useQuery(walletKey(companyId), () => getWallet(companyId), {
    enabled: Boolean(prompt) && !onPremises,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const periods = findFeature(catalog.data, feature)?.tariff.periods
  const choosesPeriod = Boolean(periods && periods.length > 1)
  const chosenMonths = choosesPeriod
    ? pickedMonths ?? periods?.find((period) => period.bought)?.months ?? periods?.find((period) => period.own)?.months
    : undefined

  const activate = async (confirmCharge: boolean, renew = renewing, months = chosenMonths, way = choice) => {
    setBusy(true)
    setError(undefined)
    setNotice('')
    setRenewing(renew)
    setChoice(way)
    try {
      const next = await activateServiceFeature(companyId, feature, confirmCharge, renew, months, way.method, way.autoRenew)
      queryClient.setQueryData(serviceCatalogKey(companyId), next)
      setPrompt(undefined)
    } catch (activationError) {
      const read = readServiceError(activationError, 'فعال‌سازی انجام نشد؛ دوباره تلاش کنید.')
      if (read.code === CHARGE_CONFIRM && !confirmCharge) {
        setPrompt({amount: read.amount || 0, items: read.items || [], message: read.message})
      } else {
        setPrompt(undefined)
        setError({
          text: read.contact ? read.contact.text : read.message,
          wallet: read.code === WALLET_INSUFFICIENT,
          balance: read.balance,
          required: read.required,
          contact: Boolean(read.contact),
          contactEnded: read.contact?.ended,
        })
        // A service switched off, or its access ended, meanwhile: the card shows it.
        queryClient.invalidateQueries(serviceCatalogKey(companyId))
      }
    } finally {
      setBusy(false)
    }
  }

  // Back from the gateway with the credit in the wallet: the activation (or renewal) that was short goes on.
  useWalletResume(resumeAction(feature), companyId, (payload) => {
    const step = (payload || {}) as {renew?: boolean; periodMonths?: number; method?: BillingMethod; autoRenew?: boolean}
    if (typeof step.periodMonths === 'number') setPickedMonths(step.periodMonths)
    activate(true, Boolean(step.renew), typeof step.periodMonths === 'number' ? step.periodMonths : undefined, {
      method: step.method,
      autoRenew: step.autoRenew,
    })
  })

  const changeAutoRenew = async (on: boolean) => {
    setBusy(true)
    setError(undefined)
    setNotice('')
    try {
      const next = await setServiceAutoRenew(companyId, feature, on)
      queryClient.setQueryData(serviceCatalogKey(companyId), next)
      setNotice(on ? 'تمدید خودکار روشن شد.' : 'تمدید خودکار خاموش شد؛ اشتراک در پایان دوره تمام می‌شود.')
    } catch (renewError) {
      setError({text: readServiceError(renewError, 'تغییر تمدید خودکار انجام نشد؛ دوباره تلاش کنید.').message, wallet: false})
    } finally {
      setBusy(false)
    }
  }

  const switchOffNow = async () => {
    setBusy(true)
    setError(undefined)
    try {
      const next = await switchOffServiceFeature(companyId, feature)
      queryClient.setQueryData(serviceCatalogKey(companyId), next)
      setConfirmingOff(false)
      setNotice(
        isContactTariff(findFeature(catalog.data, feature)?.tariff)
          ? 'سرویس غیرفعال شد؛ تا پایان دسترسی می‌توانید آن را دوباره فعال کنید.'
          : 'سرویس غیرفعال شد؛ هر وقت بخواهید می‌توانید آن را دوباره فعال کنید.'
      )
    } catch (switchOffError) {
      setConfirmingOff(false)
      setError({text: readServiceError(switchOffError, 'غیرفعال‌سازی انجام نشد؛ دوباره تلاش کنید.').message, wallet: false})
    } finally {
      setBusy(false)
    }
  }

  const item = findFeature(catalog.data, feature)
  const off = item?.tariff.switchedOff
  // A free service that must be turned on all the same (optIn) keeps its card, to be activated or switched off.
  // Nothing to buy, renew or price in the Bazaar and Myket builds of the Android app.
  if (
    onPremises ||
    hidesPayments() ||
    !item ||
    (!isPricedTariff(item.tariff) && !off && !(showFree && item.tariff.activated) && !item.tariff.optIn)
  )
    return null

  const tariff = item.tariff
  const parts = tariffParts(tariff)
  const expired = Boolean(tariff.expired)
  const note = tariffPricingNote(tariff)
  const canChange = Boolean(catalog.data?.canChange)
  // «تماس بگیرید»: the business cannot turn it on itself. With an administrator's live access it is used free until
  // the access ends; switched off by the business itself meanwhile, its own «فعال‌سازی دوباره» still works.
  const contact = isContactTariff(tariff)
  const withoutAccess = needsContact(tariff)
  const granted = contact && !withoutAccess
  const accessLeft = granted ? daysLeft(tariff.expiresAt) : undefined
  const accessEnding = granted && !off && accessLeft !== undefined && accessLeft <= RENEW_EARLY_DAYS
  // A subscription of the business's own (not a package's) that ends within two weeks may be renewed from its end.
  const left = item.active && !expired && !tariff.package?.coversActivation ? daysLeft(tariff.expiresAt) : undefined
  const renewable =
    !contact && parts.activation > 0 && Boolean(parts.period) && left !== undefined && left <= RENEW_EARLY_DAYS
  const ownOff = Boolean(off && !off.byAdministrator)
  // «ترکیبی» picked from two ways (backend 1.0.0.690+); a package that covers it, or an administrator's switch-off, keeps the plain card.
  const methodsView = offersMethods(tariff) && !contact && !tariff.package?.coversActivation && !off?.byAdministrator
  const label = tariff.label
  const stepLabel = `${renewing ? 'تمدید اشتراک' : 'فعال‌سازی'} ${label}`
  const stateClass = off
    ? off.byAdministrator
      ? 'badge-light-danger'
      : 'badge-light'
    : expired
      ? 'badge-light-danger'
      : withoutAccess || (methodsView && tariff.chooseMethod)
        ? 'badge-light-warning'
        : item.active
          ? 'badge-light-success'
          : 'badge-light-warning'
  const stateText = off
    ? 'غیرفعال‌شده'
    : withoutAccess
      ? expired
        ? 'دسترسی تمام شده'
        : 'با درخواست'
      : expired
        ? 'اشتراک تمام شده'
        : methodsView && tariff.chooseMethod
          ? 'انتخاب روش پرداخت'
          : item.active
            ? methodsView
              ? tariff.method === 'subscription'
                ? 'فعال با اشتراک'
                : 'فعال، به ازای مصرف'
              : 'فعال'
            : 'نیازمند فعال‌سازی'

  // An operation that costs nothing now: one line, opened on demand (user request 2026-10-19).
  const allowance = collapseWhenFree ? operationAllowance(item, {uses, used}) : undefined
  if (allowance?.free) {
    return (
      <section className={clsx('svc-activation svc-activation--compact', className)} aria-label={`سرویس ویژهٔ ${label}`}>
        <div className='svc-compact'>
          {badge && <ServiceBadge />}
          <strong className='svc-compact__name'>{title ?? label}</strong>
          <span className='badge badge-light-success'>رایگان</span>
          <span className='svc-compact__summary'>{allowance.summary}</span>
          <button
            type='button'
            className='btn btn-sm btn-link svc-compact__toggle'
            aria-expanded={detailsOpen}
            aria-controls={detailsId}
            onClick={() => setDetailsOpen((value) => !value)}
          >
            {detailsOpen ? 'بستن' : allowance.quota !== undefined ? 'سهمیه و قیمت' : 'قیمت'}
            <i className={clsx('las', detailsOpen ? 'la-angle-up' : 'la-angle-down')} aria-hidden='true'></i>
          </button>
        </div>
        {detailsOpen && (
          <div id={detailsId} className='svc-compact__details'>
            <dl>
              {allowance.quota !== undefined && (
                <div>
                  <dt>سهمیهٔ رایگان</dt>
                  <dd>{tariff.freeQuotaText || `${fa(allowance.quota)} مورد`}</dd>
                </div>
              )}
              {allowance.used !== undefined && (
                <div>
                  <dt>{usedLabel(allowance.scope)}</dt>
                  <dd>{fa(allowance.used)} مورد</dd>
                </div>
              )}
              {allowance.left !== undefined && (
                <div>
                  <dt>باقی‌مانده</dt>
                  <dd>{fa(allowance.left)} مورد</dd>
                </div>
              )}
              {parts.use > 0 && (
                <div>
                  <dt>{allowance.quota !== undefined ? 'پس از سهمیه، ' : ''}{tariff.useMeaning || 'هر بار استفاده'}</dt>
                  <dd>
                    <OfferPrice amount={parts.use} listAmount={parts.useList} />
                    {item.useFee !== undefined && item.useFee !== null && item.useFee !== parts.use && (
                      <span className='text-success fs-8'>، برای شما {item.useFee > 0 ? `${fa(item.useFee)} ریال` : 'رایگان'}</span>
                    )}
                  </dd>
                </div>
              )}
              {parts.activation > 0 && (
                <div>
                  <dt>{activationTitle(parts.period)}</dt>
                  <dd>
                    <OfferPrice amount={parts.activation} listAmount={parts.activationList} />
                    {item.active && tariff.expiresAtLabel && <span className='text-muted fs-8'>، اعتبار تا {tariff.expiresAtLabel}</span>}
                  </dd>
                </div>
              )}
              {tariff.dailyLimit ? (
                <div>
                  <dt>سقف روزانه</dt>
                  <dd>
                    {fa(tariff.dailyLimit)} مورد
                    {tariff.usedToday ? <span className='text-muted fs-8'>، امروز {fa(tariff.usedToday)} مورد</span> : null}
                  </dd>
                </div>
              ) : null}
            </dl>
            {note && (
              <div className='svc-activation__note'>
                <i className='las la-gift' aria-hidden='true'></i>
                {note}
              </div>
            )}
            {/* Past the allowance a «تماس بگیرید» service waits for support's access. */}
            {withoutAccess && <ContactNote tariff={tariff} />}
            {intro && <p className='svc-activation__intro mb-2'>{intro}</p>}
            <ServiceIntroLink feature={feature} label={label} />
          </div>
        )}
      </section>
    )
  }

  return (
    <section className={clsx('svc-activation', {'is-active': item.active}, className)} aria-label={`سرویس ویژهٔ ${label}`}>
      <div className='svc-activation__head'>
        {badge && <ServiceBadge />}
        <strong>{title ?? label}</strong>
        <span className={clsx('badge', stateClass)}>{stateText}</span>
        <ServiceIntroLink feature={feature} label={label} />
      </div>
      {intro && <p className='svc-activation__intro'>{intro}</p>}
      {off && (
        <div className={clsx('alert py-2 fs-7', off.byAdministrator ? 'alert-light-danger' : 'alert-light-warning')} role='note'>
          {off.message}
          {off.atLabel && <span className='text-muted fs-8'> ({off.atLabel})</span>}
          {/* Off with no access left: only support turns it on again, on request. */}
          {withoutAccess && (
            <div className='mt-2'>
              <ServiceRequestLink topic={expired ? 'AccessRenewal' : 'ServiceActivation'} feature={feature} label={label} />
            </div>
          )}
        </div>
      )}
      {methodsView && (
        <MethodSection
          feature={feature}
          label={label}
          tariff={tariff}
          useFee={item.useFee}
          canChange={canChange}
          busy={busy}
          periods={periods}
          choosesPeriod={choosesPeriod}
          chosenMonths={chosenMonths}
          onPickMonths={setPickedMonths}
          renewable={renewable}
          left={left}
          onActivate={(method, autoRenew) => activate(false, false, chosenMonths, {method, autoRenew: method === 'subscription' ? autoRenew : undefined})}
          onRenew={() => activate(false, true, chosenMonths, {})}
          onAutoRenew={changeAutoRenew}
        />
      )}
      {!methodsView && (
      <ul className='svc-activation__prices'>
        {withoutAccess && (
          <li>
            <strong>فعال‌سازی با درخواست</strong>
          </li>
        )}
        {granted && (
          <li>
            {tariff.expiresAtLabel
              ? `${off ? 'دسترسی' : 'فعال'} تا ${tariff.expiresAtLabel}`
              : isAdminOnlyFeature(tariff.feature, tariff.adminOnly)
                ? 'فعال‌شده توسط آسا، بی‌تاریخ پایان'
                : 'فعال با دسترسی پشتیبانی'}
            {accessEnding && accessLeft !== undefined && (
              <span className='text-warning fs-8'>{accessLeft > 0 ? ` (${fa(accessLeft)} روز مانده)` : ' (امروز)'}</span>
            )}
          </li>
        )}
        {parts.activation > 0 && (
          <li>
            {activationTitle(parts.period)}: <OfferPrice amount={parts.activation} listAmount={parts.activationList} />
            {item.active && tariff.expiresAtLabel && (
              <span className={clsx('fs-8', renewable ? 'text-warning' : 'text-muted')}>
                {' '}
                ، اعتبار تا {tariff.expiresAtLabel}
                {renewable && left !== undefined && (left > 0 ? ` (${fa(left)} روز مانده)` : ' (امروز)')}
              </span>
            )}
            {item.active && !tariff.expiresAtLabel && tariff.activatedAtLabel && (
              <span className='text-muted fs-8'>، پرداخت شده در {tariff.activatedAtLabel}</span>
            )}
            {expired && tariff.expiresAtLabel && <span className='text-danger fs-8'>، پایان یافته در {tariff.expiresAtLabel}</span>}
          </li>
        )}
        {!isPaidTariff(tariff) && !contact && <li>رایگان</li>}
        {parts.use > 0 && (
          <li>
            {tariff.useMeaning || 'هر بار استفاده'}: <OfferPrice amount={parts.use} listAmount={parts.useList} />
            {item.useFee !== undefined && item.useFee !== null && item.useFee !== parts.use && (
              <span className='text-success fs-8'>، برای شما {item.useFee > 0 ? `${fa(item.useFee)} ریال` : 'رایگان'}</span>
            )}
          </li>
        )}
        {tariff.usePercent ? (
          <li>
            کارمزد هر پرداخت موفق: <strong>{faPercent(tariff.usePercent)}٪</strong>
            {tariff.useMinimum ? <span>، حداقل {fa(tariff.useMinimum)} ریال</span> : null}
            {tariff.useMaximum ? <span>، حداکثر {fa(tariff.useMaximum)} ریال</span> : null}
          </li>
        ) : null}
        {/* A provider with a commission of its own (2026-10-18): its fee instead of the one above. */}
        {tariff.providers?.map((provider) => (
          <li key={provider.providerCode}>
            کارمزد پرداخت از {provider.providerName}: {provider.description}
          </li>
        ))}
        {tariff.freeQuotaText ? (
          <li>{tariff.freeQuotaText}</li>
        ) : tariff.freeQuota ? (
          <li>{fa(tariff.freeQuota)} مورد نخست رایگان است.</li>
        ) : null}
        {tariff.dailyLimit ? (
          <li>
            سقف روزانه: {fa(tariff.dailyLimit)} مورد
            {tariff.usedToday ? <span className='text-muted fs-8'>، امروز {fa(tariff.usedToday)} مورد استفاده شده</span> : null}
          </li>
        ) : null}
      </ul>
      )}
      {tariff.package && (
        <div className='svc-activation__note'>
          <i className='las la-box' aria-hidden='true'></i>
          در {tariff.package.titles.map((packageTitle) => `«${packageTitle}»`).join('، ')}
          {tariff.package.coversActivation &&
            (tariff.package.coveredUntilLabel ? `، بدون هزینهٔ فعال‌سازی تا ${tariff.package.coveredUntilLabel}` : '، بدون هزینهٔ فعال‌سازی')}
          {tariff.package.includedText && `، ${tariff.package.includedText}`}{' '}
          <Link to={businessSettingsPath(companyId, 'service-packages')}>بسته‌ها</Link>
        </div>
      )}
      {note && (
        <div className='svc-activation__note'>
          <i className='las la-gift' aria-hidden='true'></i>
          {note}
        </div>
      )}
      {walletNote && isPaidTariff(tariff) && !contact && (
        <p className='svc-activation__foot'>
          هزینه‌ها از <Link to={businessSettingsPath(companyId, 'wallet')}>کیف پول کسب‌وکار</Link> کم می‌شود.
        </p>
      )}
      {/* Where the price and «فعال‌سازی» would be, also where the page turns the service on with its own save. */}
      {withoutAccess && !off && <ContactNote tariff={tariff} />}
      {accessEnding && accessLeft !== undefined && (
        <div className='svc-contact-note' role='note'>
          <span>
            دسترسی {accessLeft > 0 ? `${fa(accessLeft)} روز دیگر` : 'امروز'} به پایان می‌رسد؛ برای تمدید، درخواست ثبت
            کنید.
          </span>
          <ServiceRequestLink topic='AccessRenewal' feature={feature} label={label} />
        </div>
      )}
      {!methodsView && !contact && !off && ((activation && !item.active) || expired || renewable) && canChange && choosesPeriod && periods && (
        <PeriodPicker
          name={`svc-period-${feature}`}
          label={label}
          periods={periods}
          value={chosenMonths}
          disabled={busy}
          onChange={setPickedMonths}
        />
      )}
      {!methodsView && !contact && !off && ((activation && !item.active) || expired || renewable) && (
        <div className='d-flex flex-wrap align-items-center gap-3'>
          {canChange ? (
            <button
              type='button'
              className={clsx('btn btn-sm', renewable ? 'btn-light-primary' : 'btn-primary')}
              disabled={busy}
              onClick={() => activate(false, renewable)}
            >
              {busy && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>}
              {expired || renewable ? `تمدید اشتراک ${label}` : `فعال‌سازی ${label}`}
            </button>
          ) : (
            <span className='text-muted fs-7'>{SERVICE_READ_ONLY_TEXT}</span>
          )}
          {renewable && canChange && <span className='text-muted fs-8'>تمدید از پایان همین دوره حساب می‌شود؛ روزی از اشتراک کم نمی‌شود.</span>}
        </div>
      )}
      {ownOff && !withoutAccess && !methodsView && (
        <div className='d-flex flex-wrap align-items-center gap-3'>
          {canChange ? (
            <button type='button' className='btn btn-sm btn-primary' disabled={busy} onClick={() => activate(false, false)}>
              {busy && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>}
              فعال‌سازی دوبارهٔ {label}
            </button>
          ) : (
            <span className='text-muted fs-7'>{SERVICE_READ_ONLY_TEXT}</span>
          )}
        </div>
      )}
      {switchOff && canChange && canSwitchOff(item) && (
        <div className='mt-2'>
          <button type='button' className='btn btn-sm btn-link text-danger px-0' disabled={busy} onClick={() => setConfirmingOff(true)}>
            <i className='las la-power-off me-1' aria-hidden='true'></i>
            غیرفعال کردن سرویس
          </button>
        </div>
      )}
      {notice && (
        <div className='alert alert-light-success py-2 mt-3 mb-0' role='status'>
          {notice}
        </div>
      )}
      {error && (
        <div className='alert alert-danger py-2 mt-3 mb-0 d-flex flex-wrap align-items-center gap-2' role='alert'>
          <span className='flex-grow-1'>{error.text}</span>
          {error.wallet && (
            <WalletShortfallAction
              companyId={companyId}
              balance={error.balance}
              required={error.required}
              actionLabel={renewing ? 'تمدید' : 'فعال‌سازی'}
              resume={{action: resumeAction(feature), payload: {renew: renewing, periodMonths: chosenMonths, ...choice}, label: stepLabel}}
            />
          )}
          {error.contact && (
            <ServiceRequestLink
              topic={error.contactEnded || expired ? 'AccessRenewal' : 'ServiceActivation'}
              feature={feature}
              label={label}
            />
          )}
        </div>
      )}
      {prompt && (
        <ChargeConfirmDialog
          prompt={prompt}
          balance={wallet.data?.balance}
          busy={busy}
          onCancel={() => setPrompt(undefined)}
          onConfirm={() => activate(true)}
          walletAction={renewing ? 'تمدید' : 'فعال‌سازی'}
          walletResume={{action: resumeAction(feature), payload: {renew: renewing, periodMonths: chosenMonths, ...choice}, label: stepLabel}}
        />
      )}
      {confirmingOff && (
        <Modal show centered onHide={busy ? () => undefined : () => setConfirmingOff(false)} aria-labelledby={`svc-off-${feature}`}>
          <Modal.Header closeButton={!busy} closeLabel='بستن'>
            <Modal.Title id={`svc-off-${feature}`} as='h2' className='fs-4'>
              غیرفعال کردن «{label}»
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className='mb-0'>
              {contact ? contactSwitchOffConsequence(label, tariff.expiresAtLabel) : switchOffConsequence(label)}
            </p>
            {switchOffEffect(feature) && <p className='mt-3 mb-0'>{switchOffEffect(feature)}</p>}
          </Modal.Body>
          <Modal.Footer>
            <button type='button' className='btn btn-light' onClick={() => setConfirmingOff(false)} disabled={busy}>
              انصراف
            </button>
            <button type='button' className='btn btn-danger' onClick={switchOffNow} disabled={busy}>
              {busy && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>}
              غیرفعال کردن
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </section>
  )
}
